import React, { useState } from "react";
import {
  BsX,
  BsMegaphone,
  BsLink45Deg,
  BsChatDots,
  BsFileText,
  BsCalendarEvent,
  BsBarChart,
  BsPlus,
  BsTrash,
  BsPin,
  BsGlobe,
  BsLock,
} from "react-icons/bs";
import { Oval } from "react-loader-spinner";

import {
  useCreateCommunityContentMutation,
  useUpdateCommunityContentMutation,
} from "../../redux/slices/api/communityApiSlice";
import notify from "../common/notify";

// Content type options
const contentTypes = [
  { value: "announcement", label: "Announcement", icon: BsMegaphone, description: "Share news and updates" },
  { value: "link", label: "Links", icon: BsLink45Deg, description: "Share useful resources" },
  { value: "discussion", label: "Discussion", icon: BsChatDots, description: "Start a conversation" },
  { value: "resource", label: "Resource", icon: BsFileText, description: "Educational content" },
  { value: "event", label: "Event", icon: BsCalendarEvent, description: "Upcoming events" },
  { value: "poll", label: "Poll", icon: BsBarChart, description: "Put something to vote" },
];

const ContentEditor = ({ communityId, editingContent, onClose }) => {
  const [type, setType] = useState(editingContent?.type || "announcement");
  const [title, setTitle] = useState(editingContent?.title || "");
  const [content, setContent] = useState(editingContent?.content || "");
  const [links, setLinks] = useState(editingContent?.links || []);
  const [pinned, setPinned] = useState(editingContent?.pinned || false);
  const [isPublic, setIsPublic] = useState(editingContent?.isPublic || false);
  const [eventDate, setEventDate] = useState(
    editingContent?.eventDetails?.date
      ? new Date(editingContent.eventDetails.date).toISOString().slice(0, 16)
      : ""
  );
  const [eventLocation, setEventLocation] = useState(editingContent?.eventDetails?.location || "");
  const [isOnline, setIsOnline] = useState(editingContent?.eventDetails?.isOnline || false);
  const [meetingUrl, setMeetingUrl] = useState(editingContent?.eventDetails?.meetingUrl || "");
  // Poll state
  const [pollOptions, setPollOptions] = useState(
    editingContent?.pollOptions?.map((opt) => opt.text || opt) || ["", ""]
  );
  const [pollEndsAt, setPollEndsAt] = useState(
    editingContent?.pollSettings?.endsAt
      ? new Date(editingContent.pollSettings.endsAt).toISOString().slice(0, 16)
      : ""
  );

  const [createContent, { isLoading: isCreating }] = useCreateCommunityContentMutation();
  const [updateContent, { isLoading: isUpdating }] = useUpdateCommunityContentMutation();

  const isLoading = isCreating || isUpdating;
  const isEditing = !!editingContent;

  const addLink = () => {
    setLinks([...links, { title: "", url: "", description: "" }]);
  };

  const updateLink = (index, field, value) => {
    const newLinks = [...links];
    newLinks[index][field] = value;
    setLinks(newLinks);
  };

  const removeLink = (index) => {
    setLinks(links.filter((_, i) => i !== index));
  };

  // Poll option handlers
  const addPollOption = () => {
    if (pollOptions.length < 10) {
      setPollOptions([...pollOptions, ""]);
    }
  };

  const updatePollOption = (index, value) => {
    const newOptions = [...pollOptions];
    newOptions[index] = value;
    setPollOptions(newOptions);
  };

  const removePollOption = (index) => {
    if (pollOptions.length > 2) {
      setPollOptions(pollOptions.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      notify("error", "Title is required");
      return;
    }

    // Validate poll options
    if (type === "poll") {
      const validPollOptions = pollOptions.filter((opt) => opt.trim());
      if (validPollOptions.length < 2) {
        notify("error", "Poll requires at least 2 options");
        return;
      }
    }

    // Validate links
    const validLinks = links.filter((l) => l.title.trim() && l.url.trim());

    const body = {
      type,
      title: title.trim(),
      content: content.trim(),
      links: validLinks,
      pinned,
      isPublic,
    };

    // Add event details if type is event
    if (type === "event" && eventDate) {
      body.eventDetails = {
        date: new Date(eventDate).toISOString(),
        location: eventLocation,
        isOnline,
        meetingUrl: isOnline ? meetingUrl : "",
      };
    }

    // Add poll options if type is poll
    if (type === "poll") {
      body.pollOptions = pollOptions.filter((opt) => opt.trim());
      body.pollSettings = {
        showResults: true,
      };
      if (pollEndsAt) {
        body.pollSettings.endsAt = new Date(pollEndsAt).toISOString();
      }
    }

    try {
      if (isEditing) {
        await updateContent({
          id: communityId,
          contentId: editingContent._id,
          body,
        }).unwrap();
        notify("success", "Content updated!");
      } else {
        await createContent({ id: communityId, body }).unwrap();
        notify("success", "Content published!");
      }
      onClose();
    } catch (err) {
      notify("error", err?.data?.message || "Failed to save content");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-800">
            {isEditing ? "Edit Content" : "Create New Content"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <BsX className="text-2xl text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Content Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content Type
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {contentTypes.map((ct) => {
                const Icon = ct.icon;
                return (
                  <button
                    key={ct.value}
                    type="button"
                    onClick={() => setType(ct.value)}
                    className={`p-3 rounded-xl border-2 text-left transition-colors ${
                      type === ct.value
                        ? "border-primary bg-purple-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <Icon
                      className={`text-lg mb-1 ${
                        type === ct.value ? "text-primary" : "text-gray-400"
                      }`}
                    />
                    <p className="font-medium text-sm text-gray-800">{ct.label}</p>
                    <p className="text-xs text-gray-500">{ct.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Give your content a title"
              maxLength={200}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write your content here..."
              rows={5}
              maxLength={10000}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
            />
            <p className="text-xs text-gray-400 mt-1 text-right">
              {content.length}/10000
            </p>
          </div>

          {/* Links Section */}
          {(type === "link" || type === "resource") && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Links
              </label>
              <div className="space-y-3">
                {links.map((link, index) => (
                  <div
                    key={index}
                    className="flex gap-2 items-start bg-gray-50 rounded-xl p-3"
                  >
                    <div className="flex-1 space-y-2">
                      <input
                        type="text"
                        value={link.title}
                        onChange={(e) => updateLink(index, "title", e.target.value)}
                        placeholder="Link title"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                      />
                      <input
                        type="url"
                        value={link.url}
                        onChange={(e) => updateLink(index, "url", e.target.value)}
                        placeholder="https://..."
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeLink(index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <BsTrash />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addLink}
                  className="inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 text-gray-500 rounded-xl hover:border-primary hover:text-primary transition-colors"
                >
                  <BsPlus />
                  Add Link
                </button>
              </div>
            </div>
          )}

          {/* Event Details */}
          {type === "event" && (
            <div className="space-y-4 bg-red-50 rounded-xl p-4">
              <h4 className="font-medium text-gray-800 flex items-center gap-2">
                <BsCalendarEvent className="text-red-500" />
                Event Details
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    value={eventLocation}
                    onChange={(e) => setEventLocation(e.target.value)}
                    placeholder="e.g., Conference Room A"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isOnline}
                  onChange={(e) => setIsOnline(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm text-gray-700">This is an online event</span>
              </label>
              {isOnline && (
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Meeting URL
                  </label>
                  <input
                    type="url"
                    value={meetingUrl}
                    onChange={(e) => setMeetingUrl(e.target.value)}
                    placeholder="https://zoom.us/..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
                  />
                </div>
              )}
            </div>
          )}

          {/* Poll Options */}
          {type === "poll" && (
            <div className="space-y-4 bg-amber-50 rounded-xl p-4">
              <h4 className="font-medium text-gray-800 flex items-center gap-2">
                <BsBarChart className="text-amber-500" />
                Poll Options
              </h4>
              <div className="space-y-2">
                {pollOptions.map((option, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-500 w-6">
                      {index + 1}.
                    </span>
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => updatePollOption(index, e.target.value)}
                      placeholder={`Option ${index + 1}`}
                      maxLength={200}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
                    />
                    {pollOptions.length > 2 && (
                      <button
                        type="button"
                        onClick={() => removePollOption(index)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <BsTrash />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {pollOptions.length < 10 && (
                <button
                  type="button"
                  onClick={addPollOption}
                  className="inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 text-gray-500 rounded-xl hover:border-primary hover:text-primary transition-colors"
                >
                  <BsPlus />
                  Add Option
                </button>
              )}
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Poll Ends (optional)
                </label>
                <input
                  type="datetime-local"
                  value={pollEndsAt}
                  onChange={(e) => setPollEndsAt(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty for no time limit
                </p>
              </div>
            </div>
          )}

          {/* Pin Toggle */}
          <label className="flex items-center gap-3 cursor-pointer p-3 bg-yellow-50 rounded-xl">
            <input
              type="checkbox"
              checked={pinned}
              onChange={(e) => setPinned(e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-400"
            />
            <div>
              <span className="font-medium text-gray-800 flex items-center gap-2">
                <BsPin className="text-yellow-600" />
                Pin this content
              </span>
              <p className="text-xs text-gray-500">
                Pinned content appears at the top of the feed
              </p>
            </div>
          </label>

          {/* Public / Private Toggle */}
          <div
            className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer border-2 transition-colors ${
              isPublic
                ? "bg-green-50 border-green-300"
                : "bg-gray-50 border-gray-200"
            }`}
            onClick={() => setIsPublic((v) => !v)}
          >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
              isPublic ? "bg-green-100" : "bg-gray-100"
            }`}>
              {isPublic ? (
                <BsGlobe className="text-green-600 text-lg" />
              ) : (
                <BsLock className="text-gray-500 text-lg" />
              )}
            </div>
            <div className="flex-1">
              <span className={`font-medium text-sm ${
                isPublic ? "text-green-800" : "text-gray-800"
              }`}>
                {isPublic ? "Public post" : "Members only"}
              </span>
              <p className="text-xs text-gray-500 mt-0.5">
                {isPublic
                  ? "Visible to everyone — no membership required"
                  : "Only NFT holders can see this post"}
              </p>
            </div>
            {/* Toggle pill */}
            <div className={`w-11 h-6 rounded-full relative transition-colors flex-shrink-0 ${
              isPublic ? "bg-green-500" : "bg-gray-300"
            }`}>
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${
                isPublic ? "left-5" : "left-0.5"
              }`} />
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="px-6 py-2.5 bg-gradient-to-r from-[#8538ff] to-[#7e7cff] text-white rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <Oval height={18} width={18} color="#fff" secondaryColor="#fff" />
                Saving...
              </>
            ) : (
              <>{isEditing ? "Update" : "Publish"}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContentEditor;
